import axios from 'axios';
import Location, { type LocationVisit } from '@/models/location';
import { isMongoObjectIdString } from '@/utils/idGeneration';
import { postParseLocationsWithDedupe } from '@/utils/parseLocationsRequestCache';
import { retryOnRateLimit } from '@/utils/retryOnRateLimit';

// const API_URL = 'http://localhost:8081';
const API_URL = process.env.NEXT_PUBLIC_APP_API_BASE_URL;

const GOOGLE_LOCATION_API = `${API_URL}/api/v2/post/parse_locations/google`;
const GET_LOCATION_BY_ID = `${API_URL}/api/v2/location`;
const GOOGLE_AUTOCOMPLETE_API = `${API_URL}/api/v2/location/autocomplete/google`;
const LOCATION_SEARCH_API = `${API_URL}/api/v1/location/search/google`;

function buildLocationAuthHeaders(): Record<string, string> | undefined {
  if (typeof window === 'undefined') return undefined;
  const rawAccessToken = localStorage.getItem('accessToken');
  if (!rawAccessToken) return undefined;
  const accessToken = rawAccessToken.startsWith('Bearer ')
    ? rawAccessToken.substring(7)
    : rawAccessToken;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Authorization: accessToken,
  };
  const rawIdToken = localStorage.getItem('idToken');
  if (rawIdToken) {
    headers['x-id-token'] = rawIdToken.startsWith('Bearer ')
      ? rawIdToken.substring(7)
      : rawIdToken;
  }
  return headers;
}

/**
 * Parameters for location autocomplete search
 */
export interface LocationAutocompleteParams {
  /** Search query text (minimum 3 characters) */
  input: string;
  /** Filter mode: 'all' for all types, 'cities' for cities only, 'default' or undefined for default 5 types */
  filterMode?: 'all' | 'cities' | 'default';
  /** Latitude for location biasing (optional) */
  lat?: number;
  /** Longitude for location biasing (optional) */
  long?: number;
}

/**
 * Parameters for Google location data with AI
 */
export interface GoogleLocationDataWithAiParams {
    /** Search title/query */
    title: string;
    /** City address for context */
    body: string;
}

async function postGoogleParseLocationsRaw(
    body: { title: string; body: string },
    photoFormat: string,
): Promise<Location[]> {
    const authHeaders = buildLocationAuthHeaders();
    const response = await axios.post<Location[] | Location>(
        `${GOOGLE_LOCATION_API}?photoFormat=${encodeURIComponent(photoFormat)}`,
        body,
        authHeaders ? { headers: authHeaders } : undefined,
    );

    return Array.isArray(response.data)
        ? response.data.map((item: unknown) => new Location(item as Record<string, unknown>))
        : [new Location(response.data as unknown as Record<string, unknown>)];
}

export const getGoogleLocationDataWithAi = async (
    body: GoogleLocationDataWithAiParams,
): Promise<Location[]> => {
    return postParseLocationsWithDedupe(
        { title: body.title, body: body.body },
        postGoogleParseLocationsRaw,
    );
};

/**
 * Fetch the persisted locations collection document for a Google Place ID.
 * List POST /api/v1/post requires a Mongo location _id / location_id for Google-backed rows;
 * scraper UI only has placeId until this resolves.
 *
 * 1) If `googlePlaceId` is already a Mongo ObjectId, GET /api/v2/location/:id to load the document.
 * 2) Otherwise POST parse_locations/google with name + address context (same upsert path as Build with AI)
 */
export async function fetchPersistedLocationByGooglePlaceId(
    googlePlaceId: string,
    context?: { name?: string; addressLine?: string }
): Promise<Location | null> {
    const gid = googlePlaceId?.trim();
    if (!gid) return null;

    // Google Place IDs (ChIJ…) never resolve via GET /api/v2/location/:id; skip the wasted request.
    if (isMongoObjectIdString(gid)) {
        try {
            const loc = await getLocationById(gid);
            if (loc?._id && isMongoObjectIdString(loc._id)) {
                return loc;
            }
        } catch {
            // Expected when document is missing or GET errors
        }
    }

    const name = (context?.name && context.name.trim()) || 'Place';
    const body =
        [context?.name, context?.addressLine].filter((s) => typeof s === 'string' && s.trim()).join('\n') ||
        gid;

    try {
        const list = await getGoogleLocationDataWithAi({
            title: name,
            body,
        });
        const arr = Array.isArray(list) ? list : [];
        const exact = arr.find((l) => l.google_place_id === gid);
        if (exact?._id && isMongoObjectIdString(exact._id)) {
            return exact;
        }
    } catch {
        return null;
    }

    return null;
}

/**
 * Fetches location autocomplete results from Google Places API
 * @param params - Search parameters including input, filterMode, and optional location biasing
 * @returns Promise resolving to array of Location objects
 * @throws Error if API request fails
 */
export const getLocationAutocomplete = async (
    params: LocationAutocompleteParams
): Promise<Location[]> => {
    const queryParams = new URLSearchParams();
    queryParams.append('input', params.input);

    if (params.filterMode) {
        queryParams.append('filterMode', params.filterMode);
    }

    if (params.lat !== undefined && params.long !== undefined) {
        queryParams.append('lat', params.lat.toString());
        queryParams.append('long', params.long.toString());
    }

    const url = `${GOOGLE_AUTOCOMPLETE_API}?${queryParams.toString()}`;
    const response = await retryOnRateLimit(
        () => axios.get<Location[] | Location>(url),
        { label: 'getLocationAutocomplete' },
    );

    if (Array.isArray(response.data)) {
        return response.data.map((item: unknown) => new Location(item as Record<string, unknown>));
    }
    return [new Location(response.data as unknown as Record<string, unknown>)];
};

/**
 * GET /api/v2/location/:id. Signed in, it also carries `recommendations[]` (every row, with
 * `user_small_avatar_url`), `rec_count`, `rec_count_breakdown` and the caller's `my_visit`.
 */
export async function getLocationById(locationId: string): Promise<Location> {
    const headers = buildLocationAuthHeaders();
    const response = await axios.get<Location>(
        `${GET_LOCATION_BY_ID}/${locationId}`,
        headers ? { headers } : undefined,
    );
    return new Location(response.data as unknown as Record<string, unknown>);
}

const LOCATION_VISIT_API = (locationId: string) =>
    `${API_URL}/api/v2/locations/${encodeURIComponent(locationId)}/visit`;

/** The visit object `{ rating, note, updated_at }` (note is "" when none), or null when there is no visit. */
function normalizeLocationVisit(data: unknown): LocationVisit | null {
    if (!data || typeof data !== 'object') return null;
    const { rating, note, updated_at } = data as Record<string, unknown>;
    if (rating !== 'not_for_me' && rating !== 'liked' && rating !== 'loved') return null;
    return {
        rating,
        note: typeof note === 'string' ? note : undefined,
        updated_at: typeof updated_at === 'string' ? updated_at : undefined,
    };
}

/** GET /api/v2/locations/:id/visit — the signed-in user's visit, or null. */
export async function getLocationVisit(locationId: string): Promise<LocationVisit | null> {
    const headers = buildLocationAuthHeaders();
    const response = await axios.get(LOCATION_VISIT_API(locationId), headers ? { headers } : undefined);
    return normalizeLocationVisit(response.data);
}

/**
 * PUT /api/v2/locations/:id/visit — one visit per user and place. `rating: null` deletes it;
 * leaving out `note` keeps the saved note. Rating "loved" also recommends the place (backend).
 */
export async function putLocationVisit(
    locationId: string,
    visit: { rating: LocationVisit['rating']; note?: string },
): Promise<LocationVisit | null> {
    const headers = buildLocationAuthHeaders();
    const response = await axios.put(LOCATION_VISIT_API(locationId), visit, headers ? { headers } : undefined);
    return normalizeLocationVisit(response.data);
}

/**
 * Search for locations using the location search endpoint (includes rec_count)
 * @param params - Search parameters including locationName, city, and optional country
 * @returns Promise resolving to array of Location objects with rec_count
 */
export interface LocationSearchParams {
    locationName: string;
    city?: string;
    country?: string;
}

export async function searchLocationByName(
    params: LocationSearchParams
): Promise<Location[]> {
    const queryParams = new URLSearchParams();
    queryParams.append('locationName', params.locationName);

    if (params.city) {
        queryParams.append('city', params.city);
    }

    if (params.country) {
        queryParams.append('country', params.country);
    }

    const url = `${LOCATION_SEARCH_API}?${queryParams.toString()}`;
    const response = await retryOnRateLimit(
        () => axios.get<Location[] | Location>(url),
        { label: 'searchLocationByName' },
    );

    if (Array.isArray(response.data)) {
        return response.data.map((item: unknown) => new Location(item as Record<string, unknown>));
    }
    return [new Location(response.data as unknown as Record<string, unknown>)];
}

/**
 * Parameters for parsing locations from text
 */
export interface ParseLocationsFromTextParams {
    /** Title (optional, can be empty string) */
    title: string;
    /** Body text containing place names to parse */
    body: string;
}

/**
 * Parses locations from plain text using Google Places API
 * @param params - Parameters including title and body text
 * @returns Promise resolving to array of Location objects
 * @throws Error if API request fails
 */
export async function parseLocationsFromText(
    params: ParseLocationsFromTextParams
): Promise<Location[]> {
    try {
        const headers = buildLocationAuthHeaders();
        if (!headers) {
            throw new Error('Authentication token not found. User must be logged in.');
        }

        return postParseLocationsWithDedupe(
            { title: params.title, body: params.body },
            async (body, photoFormat) => {
                const response = await axios.post<Location[] | Location>(
                    `${GOOGLE_LOCATION_API}?photoFormat=${encodeURIComponent(photoFormat)}`,
                    body,
                    { headers },
                );
                return Array.isArray(response.data)
                    ? response.data.map((item: unknown) => new Location(item as Record<string, unknown>))
                    : [new Location(response.data as unknown as Record<string, unknown>)];
            },
        );
    } catch (error) {
        if (axios.isAxiosError(error)) {
            const message = error.response?.data?.message || error.message || 'Failed to parse locations';
            throw new Error(message);
        }
        throw error;
    }
}

/**
 * Identity for a single parsed location. Google place id wins when present so the
 * same place collapses whether or not a Mongo id was attached. Rows with neither
 * id return null and are kept as distinct results.
 */
function locationDedupeKey(location: Location): string | null {
    const googlePlaceId = location.google_place_id?.trim();
    if (googlePlaceId) return `google:${googlePlaceId}`;
    const id = location._id?.trim();
    if (id) return `id:${id}`;
    return null;
}

/**
 * Collapse duplicate places inside one parse result, keeping the first row.
 * Same key order as mergeLocationResults: google_place_id, then _id.
 */
export function dedupeLocations(locations: Location[]): Location[] {
    const seen = new Set<string>();
    const unique: Location[] = [];
    for (const location of locations) {
        const key = locationDedupeKey(location);
        if (key) {
            if (seen.has(key)) continue;
            seen.add(key);
        }
        unique.push(location);
    }
    return unique;
}

/**
 * Merge two Location arrays, deduplicating by google_place_id (preferred) then _id.
 * `primary` results appear first; unique `secondary` results are appended.
 */
export function mergeLocationResults(primary: Location[], secondary: Location[]): Location[] {
    const key = (l: Location): string => l.google_place_id?.trim() || l._id?.trim() || '';
    const seen = new Set<string>();
    for (const l of primary) {
        const k = key(l);
        if (k) seen.add(k);
    }
    return [
        ...primary,
        ...secondary.filter((l) => {
            const k = key(l);
            return k !== '' && !seen.has(k);
        }),
    ];
}
